/**
 * UTM 参数持久化脚本
 * 用于在整个用户会话期间保持UTM追踪参数，支持Iterable等营销工具追踪
 * 
 * 功能：
 * 1. 捕获首次访问时URL中的UTM参数
 * 2. 存储到sessionStorage（会话内）和localStorage（跨会话）
 * 3. 自动为站内链接附加UTM参数
 * 4. 支持AJAX导航和动态加载的链接
 */

(function() {
  'use strict';

  // 配置：需要追踪的UTM参数列表
  const UTM_PARAMS = [
    'utm_source',
    'utm_medium', 
    'utm_campaign',
    'utm_campaignid',  // Iterable 使用的关键参数
    'utm_term',
    'utm_content',
    'utm_id'
  ];

  // 存储键名
  const STORAGE_KEY = 'suunto_utm_params';
  
  // 获取当前域名（用于判断是否为站内链接）
  const currentHost = window.location.hostname;

  /**
   * 从URL获取UTM参数
   */
  function getUtmFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const utmParams = {};
    
    UTM_PARAMS.forEach(function(param) {
      const value = params.get(param);
      if (value) {
        utmParams[param] = value;
      }
    });
    
    return utmParams;
  }

  /**
   * 从存储中获取UTM参数
   */
  function getStoredUtm() {
    try {
      // 优先使用sessionStorage（当前会话）
      let stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
      
      // 如果sessionStorage中没有，尝试从localStorage获取（跨会话）
      stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        // 检查是否在24小时内（可调整）
        if (data.timestamp && (Date.now() - data.timestamp) < 24 * 60 * 60 * 1000) {
          return data.params;
        }
      }
    } catch (e) {
      console.warn('[UTM Persistence] 读取存储失败:', e);
    }
    return {};
  }

  /**
   * 存储UTM参数
   */
  function storeUtm(utmParams) {
    if (Object.keys(utmParams).length === 0) return;
    
    try {
      // 存储到sessionStorage（会话内持久化）
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utmParams));
      
      // 同时存储到localStorage（跨会话，带时间戳）
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        params: utmParams,
        timestamp: Date.now()
      }));
      
      console.log('[UTM Persistence] 已存储UTM参数:', utmParams);
    } catch (e) {
      console.warn('[UTM Persistence] 存储失败:', e);
    }
  }

  /**
   * 合并UTM参数（URL中的优先级更高）
   */
  function mergeUtmParams(urlParams, storedParams) {
    return Object.assign({}, storedParams, urlParams);
  }

  /**
   * 判断是否为站内链接
   */
  function isInternalLink(href) {
    if (!href) return false;
    
    // 排除非HTTP链接
    if (href.startsWith('javascript:') || 
        href.startsWith('mailto:') || 
        href.startsWith('tel:') ||
        href.startsWith('#')) {
      return false;
    }
    
    try {
      const url = new URL(href, window.location.origin);
      // 检查是否为同一域名或子域名
      return url.hostname === currentHost || 
             url.hostname.endsWith('.' + currentHost) ||
             currentHost.endsWith('.' + url.hostname);
    } catch (e) {
      // 相对路径，属于站内链接
      return true;
    }
  }

  /**
   * 为URL附加UTM参数
   */
  function appendUtmToUrl(href, utmParams) {
    if (!href || Object.keys(utmParams).length === 0) return href;
    
    try {
      const url = new URL(href, window.location.origin);
      
      // 只处理站内链接
      if (!isInternalLink(href)) return href;
      
      // 添加UTM参数（如果URL中没有）
      UTM_PARAMS.forEach(function(param) {
        if (utmParams[param] && !url.searchParams.has(param)) {
          url.searchParams.set(param, utmParams[param]);
        }
      });
      
      return url.toString();
    } catch (e) {
      return href;
    }
  }

  /**
   * 处理链接点击事件
   */
  function handleLinkClick(event) {
    const link = event.target.closest('a');
    if (!link) return;
    
    const href = link.getAttribute('href');
    if (!isInternalLink(href)) return;
    
    const utmParams = getStoredUtm();
    if (Object.keys(utmParams).length === 0) return;
    
    const newHref = appendUtmToUrl(href, utmParams);
    if (newHref !== href) {
      link.setAttribute('href', newHref);
    }
  }

  /**
   * 处理表单提交
   */
  function handleFormSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    
    const action = form.getAttribute('action') || window.location.href;
    if (!isInternalLink(action)) return;
    
    const utmParams = getStoredUtm();
    if (Object.keys(utmParams).length === 0) return;
    
    // 为表单添加隐藏的UTM参数字段
    UTM_PARAMS.forEach(function(param) {
      if (utmParams[param] && !form.querySelector('input[name="' + param + '"]')) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = param;
        input.value = utmParams[param];
        form.appendChild(input);
      }
    });
  }

  /**
   * 拦截 History API（处理SPA导航）
   */
  function interceptHistoryApi() {
    const utmParams = getStoredUtm();
    if (Object.keys(utmParams).length === 0) return;

    // 拦截 pushState
    const originalPushState = history.pushState;
    history.pushState = function(state, title, url) {
      if (url && isInternalLink(url)) {
        url = appendUtmToUrl(url, utmParams);
      }
      return originalPushState.call(this, state, title, url);
    };

    // 拦截 replaceState
    const originalReplaceState = history.replaceState;
    history.replaceState = function(state, title, url) {
      if (url && isInternalLink(url)) {
        url = appendUtmToUrl(url, utmParams);
      }
      return originalReplaceState.call(this, state, title, url);
    };
  }

  /**
   * 为动态加载的链接添加UTM参数（MutationObserver）
   */
  function observeDynamicLinks() {
    const utmParams = getStoredUtm();
    if (Object.keys(utmParams).length === 0) return;

    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // 处理新添加的链接
            const links = node.tagName === 'A' ? [node] : node.querySelectorAll('a');
            links.forEach(function(link) {
              const href = link.getAttribute('href');
              if (isInternalLink(href)) {
                const newHref = appendUtmToUrl(href, utmParams);
                if (newHref !== href) {
                  link.setAttribute('href', newHref);
                }
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * 将UTM参数推送到dataLayer（用于GTM/GA追踪）
   */
  function pushToDataLayer(utmParams) {
    if (Object.keys(utmParams).length === 0) return;
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'utm_params_captured',
      ...utmParams
    });
  }

  /**
   * 初始化
   */
  function init() {
    // 1. 获取URL中的UTM参数
    const urlUtmParams = getUtmFromUrl();
    
    // 2. 获取存储的UTM参数
    const storedUtmParams = getStoredUtm();
    
    // 3. 合并参数（URL中的优先）
    const mergedParams = mergeUtmParams(urlUtmParams, storedUtmParams);
    
    // 4. 如果URL中有新的UTM参数，更新存储
    if (Object.keys(urlUtmParams).length > 0) {
      storeUtm(mergedParams);
    }
    
    // 5. 如果有UTM参数，设置事件监听器
    if (Object.keys(mergedParams).length > 0) {
      // 监听链接点击（使用捕获阶段确保在导航前处理）
      document.addEventListener('click', handleLinkClick, true);
      
      // 监听表单提交
      document.addEventListener('submit', handleFormSubmit, true);
      
      // 拦截History API
      interceptHistoryApi();
      
      // 观察动态添加的链接
      observeDynamicLinks();
      
      // 推送到dataLayer
      pushToDataLayer(mergedParams);
      
      console.log('[UTM Persistence] 已激活，当前UTM参数:', mergedParams);
    }
  }

  // 页面加载时初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // 暴露API供外部使用
  window.UtmPersistence = {
    getParams: getStoredUtm,
    appendToUrl: appendUtmToUrl,
    isInternalLink: isInternalLink
  };

})();
